import classNames from "classnames";
import React, { useEffect } from "react";

interface ToggleProps {
  id?: string;
  dataCy?: string;
  label?: string | JSX.Element;
  labelClass?: string;
  description?: string | JSX.Element;
  value: boolean;
  required?: boolean;
  readOnly?: boolean;
  error?: string;
  onToggle?: (state: boolean) => void;
  "aria-label"?: string;
  "data-test-id"?: string;
}

export const Toggle = ({
  id,
  dataCy,
  label,
  labelClass,
  description,
  value,
  onToggle,
  required,
  readOnly,
  error,
  ...props
}: ToggleProps) => {
  useEffect(() => {
    if (value == null || value == undefined) {
      onToggle && onToggle(false);
    }
  }, []);

  return (
    <div className="flex flex-col font-sans">
      <div
        onClick={() => !readOnly && onToggle && onToggle(!value)}
        id={id}
        data-cy={dataCy}
        role="switch"
        tabIndex={readOnly ? -1 : 0}
        aria-checked={Boolean(value)}
        aria-label={
          props["aria-label"] || (typeof label === "string" ? label : undefined)
        }
        onKeyDown={(event) => {
          if (readOnly || !onToggle) return;

          if (event.key === "Enter" || event.key === " ") {
            event.preventDefault();
            onToggle(!value);
          }
        }}
        className={classNames(
          "group flex items-start gap-3",
          !readOnly && "cursor-pointer",
        )}
        {...props}
      >
        <div
          data-testid="toggle"
          className={classNames(
            "relative inline-flex h-5 w-9 flex-shrink-0 items-center rounded-full border border-transparent transition-colors duration-200 ease-in-out",
            "focus:outline-none focus:ring-2 focus:ring-primary-400/20 dark:focus:ring-primary-500/20 focus:ring-offset-2",
            readOnly && "opacity-70",
            value
              ? "bg-primary-500 dark:bg-primary-400"
              : "bg-primary-200 dark:bg-primary-700",
          )}
        >
          <span
            aria-hidden="true"
            className={classNames(
              "pointer-events-none inline-block transform rounded-full bg-white shadow-sm transition duration-200 ease-in-out",
              "h-3.5 w-3.5",
              value ? "translate-x-5" : "translate-x-0.5",
              "group-hover:scale-110",
            )}
          />
        </div>

        <div className="flex flex-col">
          {label && (
            <span
              className={classNames(
                "text-sm font-medium leading-tight",
                "text-primary-800 dark:text-primary-200",
                labelClass,
              )}
            >
              {label}
              {required && (
                <span className="text-error-500 dark:text-error-400 ml-1 text-xs">
                  *
                </span>
              )}
            </span>
          )}

          {description && (
            <span className="text-primary-600/80 dark:text-primary-400/80 text-xs mt-0.5">
              {description}
            </span>
          )}
        </div>
      </div>

      {error && (
        <div
          data-test-id={`${props?.["data-test-id"]}-error`}
          className="text-error-500 dark:text-error-400 mt-1 text-xs font-medium pl-12"
        >
          {error}
        </div>
      )}
    </div>
  );
};

Toggle.displayName = "Toggle";
